import { supabaseClient } from './supabaseClient';

/**
 * Access Control API - Calls Supabase RPC functions under schema `access`
 */

// Admin Authorization
export const isAdmin = async (): Promise<boolean> => {
  try {
    console.log('🔍 Checking admin status via RPC...');
    const { data, error } = await supabaseClient
      .schema('access')
      .rpc('is_admin');
    
    if (error) {
      console.warn('⚠️ RPC function error (this is normal if function doesn\'t exist):', error.message);
      return false;
    }
    
    console.log('✓ RPC is_admin() returned:', data);
    return data ?? false;
  } catch (err) {
    console.error('❌ Exception in isAdmin:', err);
    return false;
  }
};

// User Listing (Admin Only)
export const listUsers = async () => {
  try {
    console.log('📋 Fetching users...');
    const { data, error } = await supabaseClient
      .schema('access')
      .rpc('list_users');
    
    if (error) {
      console.error('❌ Error listing users:', error);
      return [];
    }
    
    console.log('✓ Users fetched:', data);
    return data ?? [];
  } catch (err) {
    console.error('❌ Exception in listUsers:', err);
    return [];
  }
};

// User ↔ Role Management
export const assignRoleToUser = async (userId: string, roleName: string) => {
  const { error } = await supabaseClient
    .schema('access')
    .rpc('assign_role_to_user', { p_user_id: userId, p_role_name: roleName });
  if (error) {
    console.error('Error assigning role:', error);
    throw error;
  }
};

export const removeRoleFromUser = async (userId: string, roleName: string) => {
  const { error } = await supabaseClient
    .schema('access')
    .rpc('remove_role_from_user', { p_user_id: userId, p_role_name: roleName });
  if (error) {
    console.error('Error removing role:', error);
    throw error;
  }
};

export const getUserRoles = async (userId: string) => {
  const { data, error } = await supabaseClient
    .schema('access')
    .rpc('get_user_roles', { p_user_id: userId });
  if (error) {
    console.error('Error getting user roles:', error);
    return [];
  }
  return data ?? [];
};

// Role ↔ Permission Management
export const grantRolePermission = async (roleName: string, resourceCode: string, actionCode: string) => {
  const { error } = await supabaseClient
    .schema('access')
    .rpc('grant_role_permission', { p_role_name: roleName, p_resource_code: resourceCode, p_action_code: actionCode });
  if (error) {
    console.error('Error granting permission:', error);
    throw error;
  }
};

export const revokeRolePermission = async (roleName: string, resourceCode: string, actionCode: string) => {
  const { error } = await supabaseClient
    .schema('access')
    .rpc('revoke_role_permission', { p_role_name: roleName, p_resource_code: resourceCode, p_action_code: actionCode });
  if (error) {
    console.error('Error revoking permission:', error);
    throw error;
  }
};

export const getRolePermissions = async (roleName: string) => {
  const { data, error } = await supabaseClient
    .schema('access')
    .rpc('get_role_permissions', { p_role_name: roleName });
  if (error) {
    console.error('Error getting role permissions:', error);
    return [];
  }
  return data ?? [];
};

// Runtime Permission Guard
export const canUser = async (userId: string, resourceCode: string, actionCode: string): Promise<boolean> => {
  const { data, error } = await supabaseClient
    .schema('access')
    .rpc('can_user', { p_user_id: userId, p_resource_code: resourceCode, p_action_code: actionCode });
  if (error) {
    console.error('Error checking permission:', error);
    return false;
  }
  return data ?? false;
};

// Fetch reference data
export const getRoles = async () => {
  const { data, error } = await supabaseClient
    .schema('access')
    .from('roles')
    .select('*');
  if (error) {
    console.error('Error fetching roles:', error);
    return [];
  }
  return data ?? [];
};

export const getResources = async () => {
  const { data, error } = await supabaseClient
    .schema('access')
    .from('resources')
    .select('*');
  if (error) {
    console.error('Error fetching resources:', error);
    return [];
  }
  return data ?? [];
};

export const getActions = async () => {
  const { data, error } = await supabaseClient
    .schema('access')
    .from('actions')
    .select('*');
  if (error) {
    console.error('Error fetching actions:', error);
    return [];
  }
  return data ?? [];
};
